import * as React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';

/** Material UI Packages. */
import {
    Box,
    Checkbox,
    Chip,
    CircularProgress,
    FormControlLabel,
    Link,
    ListItemButton,
    TextField,
    Typography,
} from '@mui/material';

/** Internal Components. */
import { optionText } from 'shared/lib/filterLabel';

const sameOption = (a, b) => a != null && b != null && String(a.id) === String(b.id);
const toggle = (value, option) => (sameOption(value, option) ? null : option);

/** For filters that take several values: clicking an option adds it, clicking again removes it. */
const asList = (value) => (Array.isArray(value) ? value : value ? [value] : []);
const includes = (list, option) => list.some((o) => sameOption(o, option));
const toggleIn = (list, option) => (includes(list, option) ? list.filter((o) => !sameOption(o, option)) : [...list, option]);

const rowSx = {
    m: 0,
    gap: '6px',
    '& .MuiFormControlLabel-label': { fontSize: '12px', color: '#003a52' },
    '& .MuiCheckbox-root': { p: 0.25 },
};

/** Single choice shows no checkbox, only a tinted row, so nobody expects to tick several. */
const selectableRowSx = {
    py: 0.5,
    px: 1,
    borderRadius: '6px',
    fontSize: '12px',
    color: '#003a52',
    '&.Mui-selected': {
        bgcolor: 'rgba(0, 100, 126, 0.14)',
        color: '#00647e',
        fontWeight: 600,
        '&:hover': { bgcolor: 'rgba(0, 100, 126, 0.2)' },
    },
};

/** One pickable row. Also used for Google address suggestions so every filter list looks the same. */
export function SelectableOptionRow({ primary, secondary = null, selected, onClick }) {
    return (
        <ListItemButton
            dense
            disableGutters
            role="option"
            aria-selected={selected}
            selected={selected}
            onClick={onClick}
            sx={{ ...selectableRowSx, display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}
        >
            <span>{primary}</span>
            {secondary ? <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{secondary}</Typography> : null}
        </ListItemButton>
    );
}

/** One option: a tinted row for single choice (click again to clear), a checkbox for several. */
function OptionRow({ option, checked, onToggle, multiple = false }) {
    if (multiple) {
        return (
            <FormControlLabel
                sx={rowSx}
                control={<Checkbox size="small" checked={checked} onChange={onToggle} />}
                label={optionText(option)}
            />
        );
    }
    return <SelectableOptionRow primary={optionText(option)} selected={checked} onClick={onToggle} />;
}

/** A short list (e.g. Team) shown all at once as chips; one can be picked. */
export function ChipOptionList({ options, value, onChange }) {
    return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
            {options.map((option) => {
                const selected = sameOption(value, option);
                return (
                    <Chip
                        key={option.id}
                        label={optionText(option)}
                        size="small"
                        clickable
                        color={selected ? 'primary' : 'default'}
                        variant={selected ? 'filled' : 'outlined'}
                        onClick={() => onChange(toggle(value, option))}
                        sx={{ fontSize: '12px', height: 24 }}
                    />
                );
            })}
            {options.length === 0 && <Typography sx={{ fontSize: 12, color: '#7a7a7a' }}>No options</Typography>}
        </Box>
    );
}

/**
 * A list of a few dozen options (e.g. Rate Type): the first few show, the rest open with
 * "N more". `multiple` lets the operator pick several — only where the search takes a list.
 */
export function CheckboxOptionList({ options, value, onChange, visibleCount = 6, loading = false, multiple = false }) {
    const [expanded, setExpanded] = useState(false);
    const hidden = Math.max(options.length - visibleCount, 0);
    const shown = expanded || hidden === 0 ? options : options.slice(0, visibleCount);
    const selected = multiple ? asList(value) : value ? [value] : [];
    const isChecked = (option) => includes(selected, option);
    const pick = (option) => onChange(multiple ? toggleIn(asList(value), option) : toggle(value, option));
    // Chosen options must stay visible even when they sit past the fold.
    const pinned = selected.filter((o) => !includes(shown, o));

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', maxHeight: 280, overflowY: 'auto' }}>
            {loading && <CircularProgress size={16} sx={{ my: 1, alignSelf: 'center' }} />}
            {[...pinned, ...shown].map((option) => (
                <OptionRow
                    key={option.id}
                    option={option}
                    checked={isChecked(option)}
                    onToggle={() => pick(option)}
                    multiple={multiple}
                />
            ))}
            {hidden > 0 && (
                <Link
                    component="button"
                    type="button"
                    underline="hover"
                    onClick={() => setExpanded((prev) => !prev)}
                    sx={{ fontSize: 12, alignSelf: 'flex-start', mt: 0.5 }}
                >
                    {expanded ? 'Show less' : `${hidden} more`}
                </Link>
            )}
            {!loading && options.length === 0 && (
                <Typography sx={{ fontSize: 12, color: '#7a7a7a' }}>No options</Typography>
            )}
        </Box>
    );
}

/**
 * A list too long to show (e.g. trade partners, locations): the operator types to search, and
 * what they picked stays on top. `multiple` lets them pick several — only where the search takes a list.
 */
export function SearchableOptionList({
    options,
    value,
    onChange,
    onSearch,
    loading = false,
    placeholder = 'Search',
    multiple = false,
}) {
    const [term, setTerm] = useState('');
    const timer = useRef(null);
    const selected = multiple ? asList(value) : value ? [value] : [];
    const isChecked = (option) => includes(selected, option);
    const pick = (option) => onChange(multiple ? toggleIn(asList(value), option) : toggle(value, option));

    // Fetch on open and whenever the term settles (the API searches server-side).
    useEffect(() => {
        clearTimeout(timer.current);
        timer.current = setTimeout(() => onSearch(term), term ? 300 : 0);
        return () => clearTimeout(timer.current);
    }, [term, onSearch]);

    const rows = useMemo(() => {
        const rest = options.filter((o) => !includes(selected, o));
        return [...selected, ...rest];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [options, value, multiple]);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            <TextField
                size="small"
                autoFocus
                value={term}
                onChange={(event) => setTerm(event.target.value)}
                placeholder={placeholder}
                inputProps={{ 'aria-label': placeholder }}
                sx={{ '& .MuiInputBase-input': { fontSize: '12px', py: 0.5 } }}
            />
            <Box sx={{ display: 'flex', flexDirection: 'column', maxHeight: 280, overflowY: 'auto' }}>
                {loading && <CircularProgress size={16} sx={{ my: 1, alignSelf: 'center' }} />}
                {rows.map((option) => (
                    <OptionRow
                        key={option.id}
                        option={option}
                        checked={isChecked(option)}
                        onToggle={() => pick(option)}
                        multiple={multiple}
                    />
                ))}
                {!loading && rows.length === 0 && (
                    <Typography sx={{ fontSize: 12, color: '#7a7a7a' }}>No matches</Typography>
                )}
            </Box>
        </Box>
    );
}
